/** Function-execution deep-link view (chats MFE): renders the pending function card for approval/decline. The execution
 *  id is parsed from the /function/<executionId> URL path so the mount needs no extra props; identity comes from the
 *  shell reader, and unauthenticated visitors redirect to the OTP login with a redirect back once the host's sign-in has
 *  settled (`useAuthCheck`). */

import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShellIdentity, useShellRole, useShellUserId } from '@federa/components/common/federa-shell-reader';
import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { typography } from '@federa/components/css-refs.typography';
import { viewport, contentMaxWidth } from '@federa/components/css-refs.layout';
import { safeJsonParse } from '@federa/utils/json/safe-json';
import { failureMessageOrNull, getErrorMessage } from '@federa/utils/errors/error-core';
import { PAGE_ROUTES } from '@federa/const/routes';
import { DEFAULT_GUEST_ROLE } from '@federa/const/auth-session';
import { FunctionCard } from '@federa/components/domain-cards/FunctionCard';
import { getFunctionDetails } from '@federa/components/lib/api/functions.execution';
import { Spinner } from '@federa/components/feedback/Spinner';
import { Alert } from '@federa/components/feedback/Alert';
import { UNCACHED_READ_QUERY_OPTIONS } from '@federa/const/query-conditions';
import { stripIdPrefixOrKeep } from '@federa/utils/strings/user-id';
import { BaseError } from '@federa/const/base-error';
import { parseUserIdOrNull } from '@federa/utils/strings/parse';
import { ERROR_CODES } from '@federa/const/error-codes';
import { FUNCTION_EXECUTION_STATUS, resolveFunctionExecutionStatus } from '@federa/const/function-execution-status';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { FLEX_CENTER_STYLE, FLEX_COLUMN_ITEMS_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { TITLE_FUNCTION_TITLE } from '@federa/const/title-values';
import type { UserId } from '@federa/types/primitives-identifiers';
import type { FunctionDetailsState, FunctionExecutionStatus } from '@federa/types/ui-function-card';

const TITLE_FUNCTION_ACTION = 'Function Action';

function parseExecutionIdFromPath(pathname: string): string | null {
	const match = pathname.match(/\/function\/([^/?#]+)/);
	const raw = match?.[1];
	return raw ? decodeURIComponent(raw) : null;
}

/** Static fallbacks used until authoritative function details load. */
const DEFAULT_FUNCTION_VALUES: { title: string; status: FunctionExecutionStatus; argumentPayload: unknown } = {
	title: TITLE_FUNCTION_TITLE,
	status: FUNCTION_EXECUTION_STATUS.PENDING,
	argumentPayload: undefined,
};

const HEADER_WRAPPER_STYLE: CSSProperties = { marginBottom: spacing[8] };

const HEADER_TITLE_STYLE: CSSProperties = {
	fontSize: typography.fontSize.xl,
	fontWeight: typography.fontWeight.semibold,
	color: colors.text.secondary,
};

const HEADER_MESSAGE_STYLE: CSSProperties = {
	marginTop: spacing[1],
	fontSize: typography.fontSize.sm,
	color: colors.text.tertiary,
};

const STATE_PAGE_STYLE: CSSProperties = {
	padding: `${spacing[16]} ${spacing[5]}`,
	...FLEX_COLUMN_ITEMS_CENTER_STYLE,
	textAlign: 'center',
	...FULL_WIDTH_STYLE,
	maxWidth: contentMaxWidth.prose,
	margin: '0 auto',
};

const STATE_SPINNER_WRAPPER_STYLE: CSSProperties = {
	padding: spacing[8],
	...FLEX_CENTER_STYLE,
};

function PageHeader({ title, message }: { title: string; message: string }) {
	return (
		<div style={HEADER_WRAPPER_STYLE}>
			<h1 style={HEADER_TITLE_STYLE}>{title}</h1>
			<p style={HEADER_MESSAGE_STYLE}>{message}</p>
		</div>
	);
}

function StatePage({ title, message, error }: { title: string; message: string; error?: string }) {
	return (
		<main style={STATE_PAGE_STYLE}>
			<PageHeader title={title} message={message} />
			{error ? (
				<Alert variant="error" title={error}>
					Please check the execution ID and try again.
				</Alert>
			) : (
				<div style={STATE_SPINNER_WRAPPER_STYLE}>
					<Spinner size="lg" label={message} />
				</div>
			)}
		</main>
	);
}

/**
 * Auth check that redirects unauthenticated/guest visitors to OTP login with a redirect back to this deep link. Returns
 * true while still checking.
 *
 * A HOST SHELL SAYS WHEN ITS SIGN-IN HAS SETTLED (`hostSettled`), and the check waits for exactly that rather than a
 * timer: the host now signs a returning visitor in only after Firebase restores and the auth worker answers, which a
 * fixed window can lose to -- and losing it bounced a signed-in visitor to the login page. Only with NO shell at all
 * (`hostSettled` null) is the 1500ms grace window the signal left.
 */
function useAuthCheck(userId: UserId | null, role: string | null, executionId: string, hostSettled: boolean | null): boolean {
	const isAuthenticated = Boolean(userId && role && role !== DEFAULT_GUEST_ROLE);
	const [graceElapsed, setGraceElapsed] = useState(false);

	useEffect(() => {
		const step = authCheckStep({ isAuthenticated, hostSettled });
		if (step === 'done' || step === 'wait-for-host') return;
		const settle = (): void => {
			if (!userId || role === DEFAULT_GUEST_ROLE) {
				const currentPath = `/function/${executionId}`;
				window.location.assign(`${PAGE_ROUTES.AUTH_OTP}?redirect=${encodeURIComponent(currentPath)}`);
			} else {
				setGraceElapsed(true);
			}
		};
		if (step === 'settle-now') {
			settle();
			return;
		}
		const timer = setTimeout(settle, 1500);
		return () => clearTimeout(timer);
	}, [userId, role, executionId, isAuthenticated, hostSettled]);

	return !isAuthenticated && !graceElapsed;
}

/**
 * What the auth check does NOW: nothing (`done`, a signed-in viewer), wait for the host shell to settle
 * (`wait-for-host`), decide at once because the host HAS settled (`settle-now`), or -- with no shell at all -- start the
 * grace window (`grace-window`). Exported for its sibling suite: the redirect itself is effect-resident, and this
 * choice is the part that fails silently (a timer where the host's own signal exists bounced signed-in visitors).
 */
export function authCheckStep(args: {
	isAuthenticated: boolean;
	hostSettled: boolean | null;
}): 'done' | 'wait-for-host' | 'settle-now' | 'grace-window' {
	if (args.isAuthenticated) return 'done';
	if (args.hostSettled === false) return 'wait-for-host';
	if (args.hostSettled === true) return 'settle-now';
	return 'grace-window';
}

/** Cache key for the deep-link details read. `userId` is a KEY MEMBER, not just a gate: the view compares the record's
 *  `functionUserId` against the viewer, so a second identity on the same execution id must not read the first's entry. */
const FUNCTION_DETAILS_KEY = (executionId: string, userId: UserId | null) => ['chats-function-details', executionId, userId ?? ''] as const;

/** Fetch authoritative function details once a user id is available. */
function useFunctionDetails(executionId: string, userId: UserId | null) {
	const query = useQuery<FunctionDetailsState, Error>({
		...UNCACHED_READ_QUERY_OPTIONS,
		queryKey: FUNCTION_DETAILS_KEY(executionId, userId),
		enabled: Boolean(userId) && Boolean(executionId),
		queryFn: async () => {
			const result = await getFunctionDetails({ executionId });
			if (result.success && result.functionTitle) {
				return {
					title: result.functionTitle,
					// `functionStatus` is the raw `functions.status` column text off an unconstrained TEXT column -- narrow it rather than asserting membership (absent seeds 'pending', out-of-union resolves terminal).
					status: resolveFunctionExecutionStatus(result.functionStatus),
					argumentPayload: safeJsonParse(result.arguments) ?? result.arguments,
					functionUserId: result.functionUserId ?? null,
				};
			}
			throw new BaseError({
				code: ERROR_CODES.APP_FETCH_FAILED,
				message: getErrorMessage(result, 'Function not found'),
			});
		},
	});

	return {
		details: query.data ?? null,
		loading: query.isFetching,
		error: failureMessageOrNull(query.error),
	};
}

/** Merge authoritative details with static fallback values. */
function mergeWithDefaults(details: FunctionDetailsState | null) {
	return {
		title: details?.title ?? DEFAULT_FUNCTION_VALUES.title,
		status: details?.status ?? DEFAULT_FUNCTION_VALUES.status,
		argumentPayload: details?.argumentPayload ?? DEFAULT_FUNCTION_VALUES.argumentPayload,
		functionUserId: details?.functionUserId ?? null,
	};
}

const REVIEW_PAGE_STYLE: CSSProperties = {
	minHeight: viewport.minHeight,
	backgroundColor: colors.background.coolNeutral,
	...FLEX_COLUMN_ITEMS_CENTER_STYLE,
	justifyContent: 'center',
	padding: `${spacing[8]} ${spacing[4]}`,
};

const REVIEW_CARD_WRAPPER_STYLE: CSSProperties = {
	...FULL_WIDTH_STYLE,
	maxWidth: contentMaxWidth.prose,
};

const REVIEW_CAPTION_STYLE: CSSProperties = {
	marginTop: spacing[3],
	textAlign: 'center',
	fontSize: typography.fontSize.xs,
	color: colors.text.tertiary,
};

interface FunctionCardPageProps {
	executionId: string;
	title: string;
	status: FunctionExecutionStatus;
	argumentPayload: unknown;
	isOwner: boolean;
}

function FunctionCardPage({ executionId, title, status, argumentPayload, isOwner }: FunctionCardPageProps) {
	if (!isOwner) {
		return (
			<main style={REVIEW_PAGE_STYLE}>
				<div style={REVIEW_CARD_WRAPPER_STYLE}>
					<PageHeader title="Not available" message="This action is assigned to a different account." />
					<p style={REVIEW_CAPTION_STYLE}>If you think this is a mistake, please contact support.</p>
				</div>
			</main>
		);
	}
	return (
		<main style={REVIEW_PAGE_STYLE}>
			<div style={REVIEW_CARD_WRAPPER_STYLE}>
				<FunctionCard
					functionTitle={title}
					arguments={argumentPayload}
					executionId={executionId}
					status={status}
					explorePath={PAGE_ROUTES.EXPLORE}
				/>
				<p style={REVIEW_CAPTION_STYLE}>Review the details, then confirm or decline.</p>
			</div>
		</main>
	);
}

/** Function-execution deep-link view root. Parses the execution id from the URL, gates on shell auth, fetches details, and renders the function card with an ownership check. */
export function FunctionExecutionView() {
	const userId = useShellUserId();
	const role = useShellRole();
	const shell = useShellIdentity();
	const [executionId] = useState<string | null>(() => parseExecutionIdFromPath(window.location.pathname));

	if (!executionId) {
		return <StatePage title={TITLE_FUNCTION_ACTION} message="Missing execution id." error="No execution id found in the URL." />;
	}
	// Branded-ID boundary: the shell reader hands back a raw user id; the guard keeps the parse from throwing while auth state is still absent, and the brand stops useAuthCheck / useFunctionDetails taking the execution and user ids in either order.
	return (
		<AuthedFunctionView
			executionId={executionId}
			userId={parseUserIdOrNull(userId)}
			role={role}
			hostSettled={shell === null ? null : shell.isAuthInitialized}
		/>
	);
}

function AuthedFunctionView({
	executionId,
	userId,
	role,
	hostSettled,
}: {
	executionId: string;
	userId: UserId | null;
	role: string | null;
	/** Whether the host shell's sign-in has settled; null with no shell at all. */
	hostSettled: boolean | null;
}) {
	const checkingAuth = useAuthCheck(userId, role, executionId, hostSettled);
	const { details, loading, error } = useFunctionDetails(executionId, userId);
	const merged = mergeWithDefaults(details);

	if (checkingAuth) return <StatePage title={TITLE_FUNCTION_ACTION} message="Checking authentication..." />;
	if (loading) return <StatePage title={TITLE_FUNCTION_ACTION} message="Loading function details..." />;
	if (error && !details) return <StatePage title={TITLE_FUNCTION_ACTION} message="Error loading function details." error={error} />;

	const loggedInId = userId ? stripIdPrefixOrKeep(userId) : null;
	const functionOwnerId = merged.functionUserId ? stripIdPrefixOrKeep(merged.functionUserId) : null;
	const isOwner = Boolean(loggedInId && functionOwnerId && loggedInId === functionOwnerId);

	return <FunctionCardPage executionId={executionId} {...merged} isOwner={isOwner} />;
}
